import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import loadable from '@loadable/component';
import React from 'react';
import { useLocation } from 'react-router';
import { RouteConfigComponentProps, renderRoutes } from 'react-router-config';
import { AppRouteConfig } from '@cp/entities/clientWebRoutes';
import { coreCurrentClientApi } from '@cp/entities/client';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { createEventFactory } from '@cp/shared/tracking';
import { Stack } from '@cp/ds/src/components/stack';
import { Box } from '@cp/ds/src/components/box';
import { BOTTOM_MENU_HEIGHT } from 'src/widgets/bottomMenu';
import { useIsStandalone } from 'src/shared/mediaQueries';
import { STANDALONE_BOTTOM_OFFSET_PX } from 'src/shared/ui/layout';
import { MobileAppBanner } from 'src/widgets/mobileAppBanner';
import { BurgerMenu, useBurgerMenuController, useBurgerMenuState } from 'src/features/burgerMenu';
import { useWithTrack } from 'src/shared/tracking';
import { createRouteComponentWrapper } from '../../../routes/componentWrapper';
// import { CookieBar } from './components/cookieBar';
import { PageFooter } from './components/footer';
import { Embedding } from './components/embedding';

const BottomMenu = loadable(() => import('src/widgets/bottomMenu'));
const ClientMenu = loadable(() => import('src/widgets/client/current/menu'));

const WAIT_BEFORE_SCROLL_TO_HASH_TARGET_MS = 500;
const CLIENT_MENU_BOTTOM_SPACING_IN_PX = 8;

const profileClickEvent = createEventFactory(
  {
    subject: 'profile',
    action: 'click',
    goalId: 'nazhatie_na_profil_v_navigacii',
    goalName: 'Нажатие на "Профиль" в навигации',
  },
  () => {
    /* empty */
  },
);

const MobileClientMenuWrapper: React.FC = () => {
  const isLoggedIn = useIsLoggedIn();
  const { data: clientProfile } = coreCurrentClientApi.useGetProfileQuery(undefined, {
    skip: !isLoggedIn,
  });
  const isMobile = useIsMobile();
  const isStandalone = useIsStandalone();
  const burgerMenuState = useBurgerMenuState();
  const burgerMenuController = useBurgerMenuController();
  const withTrack = useWithTrack();

  const contentForAllDevices = <BurgerMenu isOpen={burgerMenuState.isVisible} onClose={burgerMenuController.close} />;

  if (!isMobile) {
    return contentForAllDevices;
  }

  return (
    <>
      {clientProfile ? (
        <ClientMenu
          sx={{ pb: `${BOTTOM_MENU_HEIGHT + CLIENT_MENU_BOTTOM_SPACING_IN_PX + (isStandalone ? STANDALONE_BOTTOM_OFFSET_PX : 0)}px` }}
        >
          {({ isMenuOpened, toggleMenu }) => (
            <BottomMenu isProfileTabActive={isMenuOpened} onProfileTabClick={withTrack(profileClickEvent, toggleMenu)} />
          )}
        </ClientMenu>
      ) : (
        <BottomMenu />
      )}
      {contentForAllDevices}
    </>
  );
};

const renderRoute: Parameters<typeof createRouteComponentWrapper>[1] = (routeElement) => {
  return (
    <Stack sx={{ minHeight: '100dvh' }}>
      <MobileAppBanner />
      <Box sx={{ flex: 1, backgroundColor: 'gray background' }}>{routeElement}</Box>
      <MobileClientMenuWrapper />
      <PageFooter />
      <Embedding />
      {/* Not used now (https://ru.yougile.com/team/2c7dacf7a810/#YOU-1911), but can by used again in future */}
      {/* <CookieBar /> */}
    </Stack>
  );
};

export const Layout: React.FC<RouteConfigComponentProps> = (props) => {
  const location = useLocation();

  const routes = React.useMemo(() => {
    return (props.route?.routes as AppRouteConfig[] | undefined)?.map((route: AppRouteConfig) => ({
      ...route,
      component: createRouteComponentWrapper(route, renderRoute),
    }));
  }, [props.route?.routes]);

  React.useEffect(() => {
    const timoutId = setTimeout(() => {
      const element = document.getElementById(location.hash.slice(1));
      element?.scrollIntoView({ behavior: 'smooth' });
    }, WAIT_BEFORE_SCROLL_TO_HASH_TARGET_MS);
    return () => {
      clearTimeout(timoutId);
    };
  }, [location.hash]);

  return <>{renderRoutes(routes)}</>;
};
